/**
 * Type derived from the POST /products endpoint.
 * */

export interface CheckoutAPIProductWithRequiredToken
    extends CheckoutAPIProductsWithoutRequiredToken,
        RequiredToken {}

export interface CheckoutAPIProducts extends CheckoutAPIProductsWithoutRequiredToken {}

interface RequiredToken {
    token: string | null
}

interface OptionalToken {
    token: string | null
}

export interface CheckoutAPIProductsWithoutRequiredToken {
    token: string | null
    debugData: DebugData
    products: CheckoutAPIProduct[]
    product?: CheckoutAPIProduct

    productCount: number
    filter: Filter[]
}

export interface Filter {
    field: string
    values: Value[]
}

export interface Value {
    value: string
    count: number
    filterCount: number
    totalCount: number
    data: Colorswatch | string
    sizeCharts?: string[]
}

export interface CheckoutAPIProduct {
    product: string
    name: string
    uri: string
    sku: string
    productSku: string
    brand: string
    brandName: string
    brandUri: string
    fit_type_fit_type?: string
    collection: string
    collectionName: string
    collectionUri: string
    variantName: string
    excerpt: string
    excerptHtml: string
    description: string
    descriptionHtml: string
    metaTitle: string
    metaDescription: string
    metaKeywords: string
    variant_sort_order_sort_order?: string
    stockUnit: string
    category: string
    centraProduct: string
    centraVariant: string
    itemQuantityMinimum: number
    itemQuantityMultipleOf: number
    price?: string
    prices?: Prices
    fit: Label[]
    priceAsNumber?: number
    priceBeforeDiscount?: string
    priceBeforeDiscountAsNumber?: number
    discountPercent?: number
    lowestPrice?: LowestPrice[]
    showAsOnSale?: boolean
    showAsNew?: boolean
    available: boolean
    itemTable: ItemTable
    items: Item[]
    categoryName: string[]
    categoryUri: string
    categories: Category[]
    media: Media
    mediaObjects: MediaObject[]
    subscriptionPlans: any[]
    modifiedAt: string
    createdAt: string
    measurementChart: any[]
    countryOrigin?: any
    color_swatch?: ColorSwatch
    lens?: Lens
    lens_guide_type?: LensGuideType
    size_guide_type?: SizeGuideType
    product_benefits_list?: string
    technical_details_detail1?: string
    technical_details_detail10?: string
    technical_details_detail11?: string
    technical_details_detail12?: string
    technical_details_detail2?: string
    technical_details_detail3?: string
    technical_details_detail4?: string
    technical_details_detail5?: string
    technical_details_detail6?: string
    technical_details_detail7?: string
    technical_details_detail8?: string
    technical_details_detail9?: string
    relation: string
    relatedProducts: RelatedProduct[] | MinimalRelatedProduct[]
    badge?: string[]
    tableMappings?: TableMappings
}

export interface MinimalRelatedProduct {
    product: string
    available: boolean
    media: MinimalMedia
    mediaObjects: MinimalMediaObject[]
    relation: string
}

export const isMinimalRelatedProduct = (
    product: RelatedProduct | MinimalRelatedProduct
): product is MinimalRelatedProduct =>
    (product as MinimalRelatedProduct).media !== undefined &&
    (product as RelatedProduct).color_swatch === undefined

interface MinimalMediaObject {
    media: number
    sources: MinimalSources
    attributes: any[] | Attributes
}

interface MinimalSources {
    mini: Mini[]
    small: Mini[]
    standard: Mini[]
    big: Mini[]
    bigger: Mini[]
    full: Mini[]
    thumb: Mini[]
}

interface Mini {
    url: string
}

interface MinimalMedia {
    mini: string[]
    small: string[]
    standard: string[]
    big: string[]
    bigger: string[]
    full: string[]
    thumb: string[]
}

export interface RelatedProduct {
    product: string
    name: string
    uri: string
    sku: string
    productSku: string
    brand: string
    brandName: string
    brandUri: string
    collection: string
    collectionUri: string
    fit_type_fit_type: string
    variantName: string
    countryOrigin: string
    excerpt: string
    excerptHtml: string
    description: string
    descriptionHtml: string
    metaTitle: string
    metaDescription: string
    variant_sort_order_sort_order: string
    metaKeywords: string
    fit: Label[]
    stockUnit: string
    category: string
    centraProduct: string
    centraVariant: string
    itemQuantityMinimum: number
    itemQuantityMultipleOf: number
    price: string
    priceAsNumber: number
    priceBeforeDiscount: string
    priceBeforeDiscountAsNumber: number
    discountPercent: number
    lowestPrice: LowestPrice[]
    showAsOnSale: boolean
    showAsNew: boolean
    available: boolean
    size_guide_type: SizeGuideType
    itemTable: ItemTable
    badge: string[]
    items: Item[]
    categoryName: string[]
    categoryUri: string
    categories: Category[]
    media: Media
    mediaObjects: MediaObject[]
    modifiedAt: string
    createdAt: string
    measurementChart: any[]
    collectionName: string
    tableMappings: TableMappings
    color_swatch: ColorSwatch
    lens: Lens
    lens_guide_type: LensGuideType
    product_benefits_list: string
    technical_details_detail1: string
    technical_details_detail2: string
    technical_details_detail3: string
    technical_details_detail4: string
    technical_details_detail5: string
    technical_details_detail6: string
    technical_details_detail7: string
    technical_details_detail8: string
    technical_details_detail9: string
    technical_details_detail10: string
    technical_details_detail11: string
    technical_details_detail12: string
    relation: string
    subscriptionPlans: any[]
}

export const isFullRelatedProduct = (
    product: RelatedProduct | MinimalRelatedProduct
): product is RelatedProduct => (product as RelatedProduct).color_swatch !== undefined

export const isCheckoutAPIProduct = (
    product: CheckoutAPIProduct | RelatedProduct | MinimalRelatedProduct
): product is CheckoutAPIProduct => {
    return "relatedProducts" in product
}

interface TableMappings {
    "Base size for filter": BaseSizeForFilter
}

export interface Lens {
    lens_name: string
    hex: string
    image?: lensImage
}

interface SizeGuideType {
    title: string
    descripton: string
    image_alt: string
    image: lensImage
    image_en_US?: lensImage
    image_fr?: lensImage
    image_ja?: lensImage
    image_de?: lensImage
}

export interface lensImage {
    width: string
    mimeType: string
    type: string
    url: string
    height: string
}

interface BaseSizeForFilter {
    unit: string
    x: X
    dividerSymbol: string
    y: any[]
}

interface X {
    xs: string
    s: string
    m: string
    l: string
}

export interface ColorSwatch {
    color_name: string
    hex: string
    filter_color: string
    isAvailable?: boolean
    uri?: string
}

interface Label {
    label: string
}

interface Colorswatch {
    color_name: string
    hex: string
    filter_color: string
}

export interface MediaObject {
    media: number
    sources: Sources
    attributes: Attributes[]
}

interface Attributes {
    image_type_usage_type: string
}

export interface Sources {
    mini: ImageSize[]
    small: ImageSize[]
    standard: ImageSize[]
    big: ImageSize[]
    bigger: ImageSize[]
    full: ImageSize[]
    thumb: ImageSize[]
}

export interface ImageSize {
    url: string
}

interface Media {
    mini: string[]
    small: string[]
    standard: string[]
    big: string[]
    bigger: string[]
    full: string[]
    thumb: string[]
}

interface Category {
    pathIds: string[]
    uri: string
    sortOrder: number
    name: string[]
    category: string
}

interface Item {
    sizeId: string
    item: string
    ean: string
    itemTableY: number
    itemTableX: number
    name: string
    upc: string
    sku: string
    stock: string
}

interface ItemTable {
    unit: string
    original: Original
    x: string[]
    y: any[]
    dividerSymbol: string
    desc: string
}

interface Original {
    x: string[]
    y: any[]
}

interface LowestPrice {
    periodDays: number
    price: string
    priceAsNumber: number
    priceBeforeDiscount: string
    priceBeforeDiscountAsNumber: number
}

interface DebugData {
    rawQuery: RawQuery
    transformProducts: TransformProducts
    transformFilter: TransformFilter
}

interface TransformFilter {
    filter: number
}

interface TransformProducts {
    dyn_url: string
    format: number
}

interface RawQuery {
    brandIds: number
    "elasticsearch query": number
}

interface LensGuideType {
    title: string
    image_alt: string
    image: lensImage
    document: Document
}

interface Document {
    type: string
    url: string
}

export interface PriceDetails {
    price: string
    priceAsNumber: number
    priceBeforeDiscount: string
    priceBeforeDiscountAsNumber: number
    lowestPrice: LowestPrice[]
    discountPercent: number
    showAsOnSale: boolean
    showAsNew: boolean
}

export interface Prices {
    [currencyId: string]: PriceDetails
}

// When recieving data from the API, we select what we want to use to make the document size smaller.
// This is the minimized version of the response data.

export type MinimizedMedia = {
    small: string[]
    standard: string[]
    big: string[]
}

export type MinimizedRelatedProduct = {
    name: string
    price: string
    uri: string
    color_swatch: ColorSwatch
    mediaObjects: MinimizedMedia
    items: string
    fit: string
    priceBeforeDiscount: string
    available: boolean
    itemTableX: string[]
    centraProduct: string
    showAsOnSale: boolean
    showAsNew: boolean
    priceAsNumber: number
    product_benefits_list: string
    variant_sort_order_sort_order: string
    lens: Lens
    badge: string[]
}

export type MinimizedCentraProduct = {
    search_result_id?: string
    recommendation_id?: string
    product_listing_result_id?: string
    name: string
    price: string
    uri: string
    color_swatch: ColorSwatch
    mediaObjects: MinimizedMedia
    badge: string[]
    fit: string
    priceBeforeDiscount: string
    available: boolean
    itemTableX: string[]
    centraProduct: string
    showAsOnSale: boolean
    showAsNew: boolean
    priceAsNumber: number
    product_benefits_list: string
    variant_sort_order_sort_order: string
    items: string
    lens: Lens
    brand?: string
    brandName?: string
    categoryName?: string[]
    inventory?: number
}

export type GroupedProducts = {
    key: string
    items: MinimizedCentraProduct[]
}
